import { useCallback, useState } from "react";
import {
  splitEntries,
  withKeywords,
  withTickerEntries,
  type TickerEntries,
} from "../builderModel";

const EMPTY: TickerEntries = { tickers: [], checks: [] };

/** Symbols the user has vouched for, upper-cased and added once each. */
function withSymbols(current: string[], symbols: string[]): string[] {
  const next = [...current];
  for (const raw of symbols) {
    const symbol = raw.trim().replace(/^\$/, "").toUpperCase();
    if (symbol && !next.includes(symbol)) next.push(symbol);
  }
  return next;
}

/**
 * The tickers and keywords typed into a builder's boxes, and the entries the
 * Tickers box is holding back for a check.
 *
 * Nothing is looked up. A symbol goes in as written; anything that reads as
 * a name, or could be either, waits as a check until the user says what it
 * is (classifyTickerEntry) — and the list can't be saved until then, since a
 * wrong guess would quietly narrow or widen it.
 */
export function useTypedTerms() {
  // One state for the Tickers box, so a paste files symbols and checks in
  // one update.
  const [tickerBox, setTickerBox] = useState<TickerEntries>(EMPTY);
  const [keywords, setKeywords] = useState<string[]>([]);

  /** Typed or pasted into the Tickers box. */
  const addTickers = useCallback(
    (text: string, isKnown: (symbol: string) => boolean) => {
      const entries = splitEntries(text);
      if (entries.length === 0) return;
      setTickerBox((prev) => withTickerEntries(prev, entries, isKnown));
    },
    []
  );

  /**
   * Symbols the user has already said are symbols: a CSV's ticker column or
   * a suggestion picked. Straight in, upper-cased.
   */
  const addSymbols = useCallback((symbols: string[]) => {
    setTickerBox((prev) => ({ ...prev, tickers: withSymbols(prev.tickers, symbols) }));
  }, []);

  /** Typed or pasted into the Keywords box. */
  const addKeywords = useCallback((text: string) => {
    const entries = splitEntries(text);
    if (entries.length === 0) return;
    setKeywords((prev) => withKeywords(prev, entries));
  }, []);

  const removeTicker = useCallback((term: string) => {
    setTickerBox((prev) => ({ ...prev, tickers: prev.tickers.filter((t) => t !== term) }));
  }, []);

  const removeKeyword = useCallback((term: string) => {
    setKeywords((prev) => prev.filter((k) => k !== term));
  }, []);

  /**
   * Settles a held-back entry: kept as a ticker, made a keyword, or removed.
   * "Find it in tags" is a removal here; the builder hands the word to the
   * Tags box.
   */
  const resolveCheck = useCallback(
    (word: string, as: "ticker" | "keyword" | "remove") => {
      setTickerBox((prev) => ({
        tickers: as === "ticker" ? withSymbols(prev.tickers, [word]) : prev.tickers,
        checks: prev.checks.filter((c) => c.word !== word),
      }));
      if (as === "keyword") setKeywords((prev) => withKeywords(prev, [word]));
    },
    []
  );

  /** Replace everything with a saved list's terms. */
  const load = useCallback((terms: { tickers?: string[]; keywords?: string[] }) => {
    setTickerBox({ tickers: terms.tickers ?? [], checks: [] });
    setKeywords(terms.keywords ?? []);
  }, []);

  const clear = useCallback(() => load({}), [load]);

  const { tickers, checks } = tickerBox;
  return {
    tickers,
    keywords,
    checks,
    hasTerms: tickers.length > 0 || keywords.length > 0,
    addTickers,
    addSymbols,
    addKeywords,
    removeTicker,
    removeKeyword,
    resolveCheck,
    load,
    clear,
  };
}

export type TypedTerms = ReturnType<typeof useTypedTerms>;
